import React, { useState, useCallback } from 'react'

interface SmartInputProps {
  type: 'text' | 'email' | 'letters-only' | 'numbers-only' | 'username' | 'mobile-digits'
  value: string
  onChange: (value: string) => void
  maxLength?: number
  placeholder?: string
  className?: string
  required?: boolean
  id?: string
  onFocus?: () => void
  onBlur?: () => void
  disabled?: boolean
}

export const SmartInput: React.FC<SmartInputProps> = ({
  type,
  value,
  onChange,
  maxLength,
  placeholder,
  className = '',
  required = false,
  id,
  onFocus,
  onBlur,
  disabled = false
}) => {
  const [showPassword, setShowPassword] = useState(false)

  // Input filtering based on type
  const filterInput = useCallback((inputValue: string): string => {
    switch (type) {
      case 'letters-only':
        // Only letters, no spaces, no numbers
        return inputValue.replace(/[^A-Za-z]/g, '')
      
      case 'numbers-only':
        // Only numbers
        return inputValue.replace(/[^0-9]/g, '')
      
      case 'mobile-digits':
        // Only 9 digits for mobile (country code handled separately)
        return inputValue.replace(/[^0-9]/g, '').slice(0, 9)
      
      case 'username':
        // ASCII characters, no spaces
        return inputValue.replace(/[^\x00-\x7F]/g, '').replace(/\s/g, '')
      
      case 'email':
        // ASCII characters, no spaces
        return inputValue.replace(/[^\x00-\x7F]/g, '').replace(/\s/g, '')
      
      case 'text':
      default:
        // ASCII characters, no spaces for password fields
        return inputValue.replace(/[^\x00-\x7F]/g, '').replace(/\s/g, '')
    }
  }, [type])

  // Handle key press to prevent invalid characters
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const char = e.key
    const currentValue = (e.target as HTMLInputElement).value
    
    // Allow control keys (backspace, delete, arrow keys, etc.)
    if (char.length > 1) return
    
    // Check if the character would be filtered out
    const testValue = currentValue + char
    const filteredValue = filterInput(testValue)
    
    // If the filtered value doesn't include the new character, prevent it
    if (filteredValue !== testValue) {
      e.preventDefault()
    }
  }, [filterInput])

  // Handle paste events
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pastedText = e.clipboardData.getData('text')
    const filteredText = filterInput(pastedText)
    onChange(filteredText)
  }, [filterInput, onChange])

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    const filteredValue = filterInput(rawValue)
    onChange(filteredValue)
  }, [filterInput, onChange])

  // Get visual indicator for input type
  const getTypeIndicator = () => {
    switch (type) {
      case 'email': return '@'
      case 'letters-only': return 'Aa'
      case 'numbers-only': return '123'
      case 'username': return 'user'
      case 'mobile-digits': return '123'
      default: return ''
    }
  }

  return (
    <div className="smart-input-container">
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onPaste={handlePaste}
        onFocus={onFocus}
        onBlur={onBlur}
        maxLength={maxLength}
        placeholder={placeholder}
        className={className}
        required={required}
        id={id}
        autoComplete="off"
        disabled={disabled}
      />
      {maxLength && value.length > 0 && (
        <div className="inline-char-counter">
          {value.length}/{maxLength}
        </div>
      )}
    </div>
  )
} 